/**
 * Shared helpers for matching K8 / middle-school URL unit segments (e.g.
 * `unit-1`, `recognizing-bullying-and-harassment`) to LMS unit slices inside a
 * v1-shaped class. Used by both the legacy TeachView path and UnitV3.
 */

import { findCourseTreeNode } from 'utils/courseTreeHelpers'

export const KNOWN_UNITS_INTERNAL_TITLE_MAP = {
  'unit-1': 'u1',
  'unit-2': 'u2',
  'unit-3': 'u3',
  'unit-4': 'u4',
  'recognizing-bullying-and-harassment': 'rbh',
}

const getUnitIndexFromUnitId = unitId => {
  const match = String(unitId || '').match(/^unit-(\d+)$/)
  if (!match) return -1

  return parseInt(match[1], 10) - 1
}

const findLmsUnitByContentfulId = (lmsClass, contentfulId) =>
  lmsClass.units.find(unit => unit.contentfulID === contentfulId) || null

/**
 * Locates the LMS unit slice for a URL unit segment inside a v1-shaped class.
 *
 * Resolution order:
 * 1. Match via course-tree `internalTitle` (legacy course-manager naming)
 * 2. Fall back to positional index (`unit-1` → index 0) for v3 trees that omit
 *    `internalTitle`
 */
export const findLmsUnitByUnitId = ({ lmsClass, courseTree, unitId }) => {
  if (!lmsClass?.units?.length || !courseTree || !unitId) return null

  const unitInternalTitle = KNOWN_UNITS_INTERNAL_TITLE_MAP[unitId]
  if (unitInternalTitle) {
    const unitNode = findCourseTreeNode(
      courseTree,
      node =>
        node.type === 'unit' &&
        String(node.internalTitle || '').includes(unitInternalTitle),
    )

    if (unitNode?.contentfulId) {
      return findLmsUnitByContentfulId(lmsClass, unitNode.contentfulId)
    }
  }

  const unitIndex = getUnitIndexFromUnitId(unitId)
  if (unitIndex >= 0 && unitIndex < lmsClass.units.length) {
    return lmsClass.units[unitIndex]
  }

  return null
}
